/**
 * PreSet Officer Appointment (Director / Secretary) flat form → OfficerAppointmentForm initial-data
 */

import { buildManualOfficerContactFields } from '@/features/startTask/utils/manualOfficerContactData';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function resolveRepeatingFirstRow(src: Record<string, unknown>): Record<string, unknown> {
  const repeating = src.repeatingData;
  const sections: unknown[][] = [
    Array.isArray(src.new_directors) ? src.new_directors : [],
    Array.isArray(src.new_secretaries) ? src.new_secretaries : [],
  ];
  if (repeating && typeof repeating === 'object' && !Array.isArray(repeating)) {
    for (const rows of Object.values(repeating as Record<string, unknown>)) {
      if (Array.isArray(rows)) sections.push(rows);
    }
  }
  for (const rows of sections) {
    const row = rows[0];
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      return row as Record<string, unknown>;
    }
  }
  return {};
}

function mergeFlatWithRepeatingRow(
  src: Record<string, unknown>,
): Record<string, unknown> {
  const firstRow = resolveRepeatingFirstRow(src);
  if (!Object.keys(firstRow).length) return src;
  return { ...firstRow, ...src };
}

/** PreSet flat 변환값이 기존 contactData 스냅샷보다 우선 (수동 입력 ID/주소/연락처 반영) */
export function mergeOfficerContactDataForPreset(
  built: Record<string, unknown>,
  existing: Record<string, unknown> | null | undefined,
  contactCd: string,
): Record<string, unknown> {
  if (!trim(built.CONTACT_NAME)) {
    return existing && typeof existing === 'object' ? { ...existing } : built;
  }
  const merged = {
    ...(existing && typeof existing === 'object' ? existing : {}),
    ...built,
  };
  if (contactCd) {
    merged.contactCd = contactCd;
    merged.officerNameCd = contactCd;
  }
  return merged;
}

/** PreSet / 문서 flat 필드 → OfficerAppointmentForm contactData */
export function buildOfficerAppointmentContactDataFromFlat(
  src: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const safe = src && typeof src === 'object' ? src : {};
  const preset = safe.presetForm && typeof safe.presetForm === 'object'
    ? (safe.presetForm as Record<string, unknown>)
    : {};
  const cd = safe.contactData && typeof safe.contactData === 'object'
    ? (safe.contactData as Record<string, unknown>)
    : {};
  const merged = mergeFlatWithRepeatingRow({
    ...cd,
    ...preset,
    ...safe,
  });
  return buildManualOfficerContactFields(merged) as unknown as Record<string, unknown>;
}

/** L_TASK_EFILING_DATA seed — 사용자가 e-Filing UI 에서 저장한 nested 폼이 아님 */
export function isOfficerAppointmentPresetSeedFormData(
  fd: Record<string, unknown> | null | undefined,
): boolean {
  if (!fd || typeof fd !== 'object') return false;
  if (fd.formFields || fd.saveOfficer) return false;
  return Boolean(
    fd.directorName
    || fd.director_name
    || fd.secretary_name
    || fd.secretaryName
    || fd.officerName
    || fd.director_email
    || fd.directorEmail,
  );
}

export function adaptOfficerAppointmentFormData(
  src: Record<string, unknown> | null | undefined,
  entityCdFallback = '',
  entityNameFallback = '',
): Record<string, unknown> {
  const safe = src && typeof src === 'object' ? src : {};
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = safe[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return '';
  };

  const contactCd = trim(pick('officerNameCd', 'officer_name_cd', 'contactCd', 'officerCode'));
  const builtContact = buildOfficerAppointmentContactDataFromFlat(safe);
  const existingContact = safe.contactData as Record<string, unknown> | undefined;
  const contactData = mergeOfficerContactDataForPreset(builtContact, existingContact, contactCd);

  const directorName = pick('directorName', 'director_name', 'officerName', 'secretary_name');
  const appointmentDate = pick(
    'appointment_effective_date',
    'appointmentEffectiveDate',
    'appointedDate',
  );
  const appointedValue =
    appointmentDate && typeof appointmentDate === 'object' && appointmentDate !== null && 'value' in (appointmentDate as object)
      ? (appointmentDate as { value: string }).value
      : String(appointmentDate || '').trim();

  return {
    ...safe,
    entity: trim(pick('entity', 'entityCd')) || entityCdFallback,
    entityName: {
      value: trim(pick('company_name', 'companyName', 'entityName')) || entityNameFallback,
    },
    entityRegNo: {
      value: pick('company_registration_no', 'companyRegistrationNo', 'entityRegNo', 'entityUEN'),
    },
    position: pick('position', 'positionCd') || 'B30027',
    positionCd: pick('position', 'positionCd') || 'B30027',
    officerNameCd: contactCd,
    officerName: directorName,
    directorName,
    directorIdentificationNo: pick(
      'director_identification_no',
      'directorIdentificationNo',
      'secretary_identification_no',
    ),
    directorNationality: pick(
      'director_nationality',
      'directorNationality',
      'secretary_nationality',
      'secretaryNationality',
    ),
    directorDateOfBirth: pick('director_date_of_birth', 'directorDateOfBirth'),
    directorEmail: pick('director_email', 'directorEmail'),
    directorContactNo: pick('director_contact_no', 'directorContactNo'),
    directorAddress: pick('director_address', 'directorAddress', 'secretary_address', 'secretaryAddress'),
    directorAlternateAddress: pick('director_alternate_address', 'directorAlternateAddress'),
    appointedDate: { value: appointedValue },
    appointmentEffectiveDate: appointedValue,
    contactData,
    entityUEN: pick('entityUEN', 'company_registration_no', 'companyRegistrationNo'),
    filingAgentUEN: pick('filingAgentUEN'),
  };
}

export function enrichOfficerAppointmentSeedFormData(
  fd: Record<string, unknown>,
): Record<string, unknown> {
  const contactCd = trim(fd.contactCd ?? fd.officerNameCd ?? fd.officerCode);
  const built = buildOfficerAppointmentContactDataFromFlat(fd);
  const existingContact = fd.contactData as Record<string, unknown> | undefined;
  const contactData = mergeOfficerContactDataForPreset(built, existingContact, contactCd);

  return {
    ...fd,
    contactData: contactData ?? fd.contactData,
    officerNameCd: contactCd,
    officerCode: contactCd,
    contactCd,
  };
}
